import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { isSessionRevokedError } from '@/lib/sessionValidation';
import { isBookingGuestProfile } from '@/lib/guestGate';
import type { UserProfile } from '@/types';

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  isAdmin: boolean;
  /** Sant när en bokningsgäst just nekats — inloggningssidan förklarar varför. */
  guestBlocked: boolean;
}

interface AuthContextValue extends AuthState {
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const ADMIN_ROLES = ['superadmin', 'partner_admin', 'customer_admin'];

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    profile: null,
    loading: true,
    isAdmin: false,
    guestBlocked: false,
  });

  const fetchProfile = useCallback(async (userId: string): Promise<UserProfile | null> => {
    const [{ data: roleData }, { data: profileData }] = await Promise.all([
      supabase
        .from('user_role_assignments')
        .select('role, organization_id')
        .eq('user_id', userId)
        .eq('is_active', true),
      supabase
        .from('profiles_v2')
        .select('display_name, user_type')
        .eq('id', userId)
        .maybeSingle(),
    ]);

    const roles = roleData?.map((r) => r.role) ?? [];
    const organizationId = roleData?.[0]?.organization_id ?? null;

    return {
      id: userId,
      display_name: profileData?.display_name ?? null,
      email: null,
      organization_id: organizationId,
      roles,
      is_booking_guest: isBookingGuestProfile(profileData),
    };
  }, []);

  const userId = state.user?.id;
  const refreshProfile = useCallback(async () => {
    if (!userId) return;
    const profile = await fetchProfile(userId);
    if (profile) {
      setState((prev) => ({
        ...prev,
        profile,
        isAdmin: profile.roles.some((r) => ADMIN_ROLES.includes(r)),
      }));
    }
  }, [userId, fetchProfile]);

  // Session bootstrap. Resolve `loading`/`user` synchronously from the session
  // (via an explicit getSession on mount + auth-state changes) so the UI never
  // depends on the slower profile/role query. Without the initial getSession a
  // cold SPA mount can sit on `loading: true` until a hard reload fires the
  // INITIAL_SESSION event — which is the "spinner until refresh" symptom.
  useEffect(() => {
    let mounted = true;

    const applySession = (session: Session | null) => {
      if (!mounted) return;
      setState((prev) => ({
        ...prev,
        user: session?.user ?? null,
        session: session ?? null,
        loading: false,
        // Clear profile/admin on sign-out; the effect below (re)loads it on sign-in.
        ...(session ? null : { profile: null, isAdmin: false }),
      }));
    };

    // Servervalidera den cachade sessionen. getSession() läser bara
    // localStorage — en användare vars konto raderats/återkallats på servern
    // ser annars inloggat läge tills access-token hinner gå ut (~1 h).
    // Endast ett explicit avvisande från auth-servern rensar sessionen;
    // nätverksfel (offline-PWA) lämnar den orörd.
    const revalidateSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (!data.session) return;
      const { error } = await supabase.auth.getUser();
      if (isSessionRevokedError(error)) {
        // Lokal signOut räcker: servern har redan avvisat sessionen.
        // Triggar SIGNED_OUT → applySession(null) via onAuthStateChange.
        await supabase.auth.signOut({ scope: 'local' });
      }
    };

    supabase.auth
      .getSession()
      .then(({ data }) => {
        applySession(data.session);
        if (data.session) void revalidateSession();
      })
      .catch(() => applySession(null));

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => applySession(session)
    );

    // Fånga återkallelse medan appen legat i bakgrunden (PWA återöppnas
    // t.ex. via en inbjudningslänk utan att komponenten monteras om).
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void revalidateSession();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      mounted = false;
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // Load profile/roles whenever the authenticated user changes. Decoupled from
  // the `loading` gate above: a slow or failing profile query degrades to "no
  // admin extras" instead of blocking the whole app on a spinner. Admin UI is
  // UX-only — backend RBAC remains the security boundary (CLAUDE.md §5.4).
  useEffect(() => {
    if (!userId) return;
    let mounted = true;

    fetchProfile(userId)
      .then(async (profile) => {
        if (!mounted || !profile) return;
        // En bokningsgäst hör inte hemma här: logga ut lokalt och låt
        // inloggningssidan hänvisa till nyckellänken i SMS/e-post.
        if (profile.is_booking_guest) {
          setState((prev) => ({ ...prev, guestBlocked: true }));
          await supabase.auth.signOut({ scope: 'local' });
          // Sidan har oftast redan hunnit navigera vidare (t.ex. /keys) när
          // profilen kommer — ta gästen tillbaka till inloggningen med
          // förklaringen i URL:en så den överlever navigeringen.
          if (!window.location.pathname.startsWith('/auth')) {
            window.location.replace('/auth?blocked=guest');
          }
          return;
        }
        setState((prev) => ({
          ...prev,
          profile,
          isAdmin: profile.roles.some((r) => ADMIN_ROLES.includes(r)),
          guestBlocked: false,
        }));
      })
      .catch(() => {
        /* Non-fatal: keep the user signed in without admin extras. */
      });

    return () => {
      mounted = false;
    };
  }, [userId, fetchProfile]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut({ scope: 'local' });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
